import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { ArrowLeft, ArrowRight, Save, Home } from 'lucide-react';
import { ReactNode } from 'react';
import React from 'react';
import { useNavigate } from 'react-router-dom';

interface SectionWrapperProps {
  title: string;
  description: string;
  icon: ReactNode;
  children: ReactNode;
  onNext?: () => void | Promise<void>;
  onSkip?: () => void | Promise<void>;
  onSave?: () => void | Promise<void>;
  onSaveAndReturn?: () => void | Promise<void>;
  canSkip?: boolean;
  nextLabel?: string;
  isNextDisabled?: boolean;
  showSaveButton?: boolean;
  showSaveAndReturn?: boolean;
  onPrevious?: () => void | Promise<void>;
}

export const SectionWrapper = ({
  title,
  description,
  icon,
  children,
  onNext,
  onSkip,
  canSkip = false,
  nextLabel = 'Continue',
  isNextDisabled = false,
  showSaveButton = false,
  showSaveAndReturn = true,
  onSave,
  onSaveAndReturn,
  onPrevious,
}: SectionWrapperProps) => {
  const navigate = useNavigate();
  
  // For Phase 2, all handlers are passed as props, so we don't need context
  // For Phase 1, try to use context but handle gracefully if not available
  let onboardingContext: any = null;
  
  // Only try to use context if we're in Phase 1 (when onPrevious/onNext aren't provided)
  // In Phase 2, all navigation is handled via props, so context is not needed
  if (!onPrevious && !onNext) {
    // Phase 1 - try to use context
    try {
      // eslint-disable-next-line @typescript-eslint/no-var-requires
      const { useOnboarding } = require('@/contexts/OnboardingContext');
      onboardingContext = useOnboarding();
    } catch {
      // Context provider not available, that's okay - will use default behavior
    }
  }
  // Phase 2 - use props only, no context needed
  
  const [isNavigating, setIsNavigating] = React.useState(false);

  const handleNext = async () => {
    if (isNavigating) return; // Prevent double-clicks
    
    setIsNavigating(true);
    try {
      if (onNext) {
        await onNext();
        // Note: onNext typically calls completeSection() which automatically advances
        // So we don't need to call nextSection() here to avoid double-advance
      } else if (onboardingContext?.nextSection) {
        // Only advance if there's no onNext handler and context is available
        await onboardingContext.nextSection();
      }
    } catch (error) {
      console.error('Error in handleNext:', error);
      // Don't navigate if there's an error
    } finally {
      setIsNavigating(false);
    }
  };

  const handleSkip = async () => {
    if (isNavigating) return; // Prevent double-clicks
    
    setIsNavigating(true);
    try {
      if (onSkip) {
        await onSkip();
        // Note: onSkip typically calls skipSection() which automatically advances
        // So we don't need to call nextSection() here to avoid double-advance
      } else if (onboardingContext?.nextSection) {
        // Only advance if there's no onSkip handler and context is available
        await onboardingContext.nextSection();
      }
    } catch (error) {
      console.error('Error in handleSkip:', error);
      // Don't navigate if there's an error
    } finally {
      setIsNavigating(false);
    }
  };

  const handlePrevious = async () => {
    if (isNavigating) return; // Prevent double-clicks
    
    setIsNavigating(true);
    try {
      if (onPrevious) {
        await onPrevious();
      } else if (onboardingContext?.previousSection) {
        await onboardingContext.previousSection();
      }
    } catch (error) {
      console.error('Error in handlePrevious:', error);
    } finally {
      setIsNavigating(false);
    }
  };

  const handleSave = async () => {
    if (onSave) {
      try {
        await onSave();
      } catch (error) {
        console.error('Error in handleSave:', error);
      }
    }
  };

  const handleSaveAndReturn = async () => {
    if (isNavigating) return;
    
    setIsNavigating(true);
    try {
      // First save progress - prioritize onSave handler from section
      if (onSave) {
        // Call the section's save handler first to save all data
        await onSave();
      } else if (onSaveAndReturn) {
        // If no onSave, use onSaveAndReturn if provided
        await onSaveAndReturn();
      }
      
      // Navigate back to onboarding hub
      navigate('/onboarding');
    } catch (error) {
      console.error('Error in handleSaveAndReturn:', error);
      // Still navigate even if save fails
      navigate('/onboarding');
    } finally {
      setIsNavigating(false);
    }
  };

  // Determine if back button should be disabled
  // In Phase 2, onPrevious is provided as prop, so check that
  // In Phase 1, check context state
  const isBackDisabled = isNavigating || 
    (onPrevious ? false : (!onboardingContext || (onboardingContext.state?.currentSection === 1)));

  return (
    <Card className="p-8">
      {/* Header */}
      <div className="flex items-start gap-4 mb-8">
        <div className="p-3 rounded-lg bg-primary/10 text-primary">
          {icon}
        </div>
        <div className="flex-1">
          <h2 className="text-2xl font-bold mb-2">{title}</h2>
          <p className="text-muted-foreground">{description}</p>
        </div>
      </div>

      {/* Content */}
      <div className="mb-8">
        {children}
      </div>

      {/* Actions */}
      <div className="grid grid-cols-3 items-center pt-6 border-t">
        {/* Left: Back button (module navigation) */}
        <div className="flex justify-start">
          <Button
            variant="ghost"
            onClick={handlePrevious}
            disabled={isBackDisabled}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
        </div>

        {/* Center: Save and Return to Dashboard buttons */}
        <div className="flex items-center justify-center gap-3">
          {onSave && showSaveButton && (
            <Button
              variant="outline"
              onClick={handleSave}
              disabled={isNavigating}
            >
              <Save className="h-4 w-4 mr-2" />
              Save Current Progress
            </Button>
          )}
          
          {showSaveAndReturn && (
            <Button
              variant="outline"
              onClick={handleSaveAndReturn}
              disabled={isNavigating}
            >
              <Home className="h-4 w-4 mr-2" />
              Return to Onboarding Dashboard
            </Button>
          )}
        </div>

        {/* Right: Continue button (module navigation) */}
        <div className="flex justify-end">
          {onNext && (
            <Button
              onClick={handleNext}
              disabled={isNextDisabled || isNavigating}
            >
              {nextLabel}
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          )}
        </div>
      </div>
    </Card>
  );
};
